// src/components/MembersArea.js
import React, { useState, useRef, useEffect } from 'react';
import TaskManager from './TaskManager';
import LpShortcuts from './LandingPageShortcuts';
import { sendMessageToOpenAI, getTaskHelp } from '../services/openAIService';

// Import all icons
import yahoo from '../assets/images/logos/yahoomail.svg';
import youtube from '../assets/images/logos/new-youtube-logo.svg';
import gmail from '../assets/images/logos/gmail.svg';
import aol from '../assets/images/logos/aol.svg';
import facebook from '../assets/images/logos/facebook.svg';
import xLogo from '../assets/images/logos/x-2.svg';
import instagram from '../assets/images/logos/instagram.svg';
import zoom from '../assets/images/logos/zoom.svg';
import tiktok from '../assets/images/logos/tiktok.svg';
import whatsapp from '../assets/images/logos/whatsapp.svg';
import linkedin from '../assets/images/logos/linkedin.svg';
import snapchat from '../assets/images/logos/snapchat.svg';
import proton from '../assets/images/logos/proton.svg';
import pinterest from '../assets/images/logos/pinterest.svg';
import reddit from '../assets/images/logos/reddit.svg';
import wechat from '../assets/images/logos/wechat.svg';
import telegram from '../assets/images/logos/telegram.svg';
import skype from '../assets/images/logos/skype.svg';
import slack from '../assets/images/logos/slack.svg';
import vimeo from '../assets/images/logos/vimeo.svg';
import tumblr from '../assets/images/logos/tumblr.svg';
import discord from '../assets/images/logos/discord.svg';
import signal from '../assets/images/logos/signal.svg';
import viber from '../assets/images/logos/viber.svg';

const allIcons = [
  { name: 'Yahoo Mail', file: yahoo, url: 'https://mail.yahoo.com' },
  { name: 'YouTube', file: youtube, url: 'https://youtube.com' },
  { name: 'Gmail', file: gmail, url: 'https://mail.google.com' },
  { name: 'AOL', file: aol, url: 'https://mail.aol.com' },
  { name: 'Facebook', file: facebook, url: 'https://facebook.com' },
  { name: 'X (Twitter)', file: xLogo, url: 'https://twitter.com' },
  { name: 'Instagram', file: instagram, url: 'https://instagram.com' },
  { name: 'Zoom', file: zoom, url: 'https://zoom.us' },
  { name: 'TikTok', file: tiktok, url: 'https://tiktok.com' },
  { name: 'WhatsApp', file: whatsapp, url: 'https://whatsapp.com' },
  { name: 'LinkedIn', file: linkedin, url: 'https://linkedin.com' },
  { name: 'Snapchat', file: snapchat, url: 'https://snapchat.com' },
  { name: 'ProtonMail', file: proton, url: 'https://proton.me' },
  { name: 'Pinterest', file: pinterest, url: 'https://pinterest.com' },
  { name: 'Reddit', file: reddit, url: 'https://reddit.com' },
  { name: 'WeChat', file: wechat, url: 'https://wechat.com' },
  { name: 'Telegram', file: telegram, url: 'https://telegram.org' },
  { name: 'Skype', file: skype, url: 'https://skype.com' },
  { name: 'Slack', file: slack, url: 'https://slack.com' },
  { name: 'Vimeo', file: vimeo, url: 'https://vimeo.com' },
  { name: 'Tumblr', file: tumblr, url: 'https://tumblr.com' },
  { name: 'Discord', file: discord, url: 'https://discord.com' },
  { name: 'Signal', file: signal, url: 'https://signal.org' },
  { name: 'Viber', file: viber, url: 'https://viber.com' }
];

const LoadingMessage = () => (
  <div className="loading-message">
    <span>Reviewing task, just a moment...</span>
    <div className="loading-dots">
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
    </div>
  </div>
);

export default function MembersArea() {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([
    { role: 'assistant', content: 'Hello! How can I help you today?' }
  ]);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const gridRef = useRef(null);

  // Get saved shortcuts from localStorage
  const [savedShortcuts] = useState(() => {
    const saved = localStorage.getItem('selectedIcons');
    return saved ? JSON.parse(saved) : [];
  });

  // Modified grid adjustment effect
  useEffect(() => {
    const adjustGridColumns = () => {
      const grid = gridRef.current;
      if (!grid) return;

      const section = grid.closest('.members-shortcuts-section');
      if (!section) return;

      const availableHeight = section.offsetHeight - 100;
      const containerWidth = grid.offsetWidth;
      const gap = 16;
      const items = savedShortcuts.length;
      let columns = 3;
      
      const itemWidth = (containerWidth - (gap * (columns - 1))) / columns;
      const itemHeight = (itemWidth * 10) / 16 + gap;
      const rowsNeeded = Math.ceil(items / columns);
      const totalHeightNeeded = rowsNeeded * itemHeight;

      if (totalHeightNeeded > availableHeight) {
        while (columns < 6) {
          const nextColumns = columns + 1;
          const nextItemWidth = (containerWidth - (gap * (nextColumns - 1))) / nextColumns;
          const nextItemHeight = (nextItemWidth * 10) / 16 + gap;
          const nextRowsNeeded = Math.ceil(items / nextColumns);
          const nextTotalHeight = nextRowsNeeded * nextItemHeight;

          if (nextTotalHeight <= availableHeight) {
            columns = nextColumns;
            break;
          }
          columns++;
        }
      }

      grid.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;
    };

    adjustGridColumns();
    window.addEventListener('resize', adjustGridColumns);
    return () => window.removeEventListener('resize', adjustGridColumns);
  }, [savedShortcuts.length]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: "smooth",
        block: "end" 
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    setMessage('');
    setIsLoading(true);

    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      const messages = chatHistory.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      messages.push({ role: 'user', content: userMessage });

      const response = await sendMessageToOpenAI(messages);
      setChatHistory(prev => [...prev, { role: 'assistant', content: response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatHistory(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an error. Please try again.' 
      }]);
    } finally {
      setIsLoading(false);
      scrollToBottom();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAiHelp = async (task) => {
    if (isLoading) return;
    setIsLoading(true);
    
    // Add loading message to chat
    setChatHistory([
      { 
        role: 'assistant', 
        content: <LoadingMessage /> 
      }
    ]);

    try {
      const response = await getTaskHelp(task);
      setChatHistory([
        { role: 'assistant', content: response }
      ]);
    } catch (error) {
      console.error('Task help error:', error);
      setChatHistory([
        { 
          role: 'assistant', 
          content: 'I apologize, but I encountered an error while analyzing your task. Please try again.' 
        }
      ]);
    } finally {
      setIsLoading(false);
      scrollToBottom();
    }
  };

  const handleAddMoreShortcuts = () => {
    setShowShortcutsModal(true);
  };

  const closeShortcutsModal = () => {
    setShowShortcutsModal(false);
  };

  return (
    <div className="members-page-container">
      <div className="logo-container">
        <span className="logo-text">Taskify</span>
        <svg 
          width="28" 
          height="28" 
          viewBox="0 0 24 24" 
          className="checkmark-icon"
        >
          <path d="M20 6L9 17L4 12"></path>
        </svg>
      </div>

      <div className="members-content-layout">
        <div className="members-shortcuts-section">
          <h2 className="section-header">Favorite Shortcuts</h2>
          <div className="shortcuts-container">
            <div className="members-shortcuts-grid" ref={gridRef}>
              {allIcons
                .filter((icon) => savedShortcuts.includes(icon.name))
                .map((icon) => (
                  <a
                    key={icon.name}
                    href={icon.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shortcut-item"
                  >
                    <div className="icon-wrapper">
                      <img 
                        src={icon.file} 
                        alt={icon.name} 
                        className="icon"
                      />
                    </div>
                  </a>
                ))}
            </div>

            <div className="shortcuts-actions">
              <button 
                onClick={handleAddMoreShortcuts}
                className="add-shortcuts-button"
              >
                + Add more shortcuts
              </button>
            </div>
          </div>
          
          <TaskManager onAiHelp={handleAiHelp} />
        </div>

        <div className="members-chat-wrapper">
          <h2 className="section-header">Smart Chat Assist, ask anything!</h2>
          <div className="members-chat-section">
            <div className="members-chat-messages">
              {chatHistory.map((msg, index) => (
                <div 
                  key={index} 
                  className={`chat-message ${msg.role}`}
                >
                  {msg.content}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div className="members-chat-input-container">
              <form 
                onSubmit={handleSendMessage} 
                className="members-chat-form"
              >
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isLoading ? "Please wait..." : "Type your message..."}
                  className="members-chat-input"
                  disabled={isLoading}
                />
                <button 
                  type="submit"
                  className="members-chat-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {showShortcutsModal && (
        <div className="modal-overlay" onClick={closeShortcutsModal}>
          <div 
            className="modal-content shortcuts-modal" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shortcuts-modal-header">
              <h2>Select Additional Shortcuts</h2>
              <button 
                className="modal-close" 
                onClick={closeShortcutsModal}
              >
                ×
              </button>
            </div>
            <div className="shortcuts-modal-content">
              <LpShortcuts buyFreeTrial={closeShortcutsModal} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}